import { Component, OnInit } from '@angular/core';
import { AgFns, GridOptionsExt, BaseService, ISortModel } from '@core';
import { AccountDbQueryService, AccountDbSaveService } from '@account';
import { ColDef, GridReadyEvent } from '@ag-grid-community/core';
import { ProximityUser, ProximityUserNote } from '@models';
import { UtilFns } from '@utils';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { ProximityUserNoteDialogComponent } from './proximity-user-note-dialog';

export interface ProximityUserNotesDialogParams {
  proximityUser: ProximityUser;
  isEditable: boolean;
}

@Component({
  selector: 'prox-proximity-user-notes-dialog',
  templateUrl: './proximity-user-notes-dialog.html',
  standalone: false,
})
export class ProximityUserNotesDialogComponent implements OnInit {
  UtilFns = UtilFns;

  proximityUser!: ProximityUser;
  isEditable = true;

  gridOptions: GridOptionsExt = {};
  notes: ProximityUserNote[] = [];

  isPageReady = false;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig<ProximityUserNotesDialogParams>,
    public dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    public pngDialogService: DialogService,
    public baseService: BaseService,
  ) {}

  public static async openDialog(
    dialogService: DialogService,
    data?: ProximityUserNotesDialogParams,
    config?: DynamicDialogConfig<ProximityUserNotesDialogParams>,
  ) {
    // data = { { width: '90vw' }, ...data }
    const ref = dialogService.open(ProximityUserNotesDialogComponent, {
      closable: true,
      draggable: true,
      modal: true,
      width: '60vw',
      height: '80vh',
      // baseZIndex: 10000,
      focusOnShow: false,
      ...config,
      data: data,
      header: `Notes for Proximity User - ${data?.proximityUser.fullName}`,
    });
    const r = await firstValueFrom(ref.onClose);
    return r;
  }

  async ngOnInit() {
    const params = this.config.data;
    UtilFns.assertNonNull(params);

    this.proximityUser = params.proximityUser;
    this.isEditable = params.isEditable;
    this.notes = await this.dbQueryService.getProximityUserNotes(this.proximityUser.id);

    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
    });

    this.isPageReady = true;
  }

  onGridReady(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      { headerName: 'Date', field: 'noteTs', ...AgFns.fixWidth('ts') },
      { headerName: 'Note', field: 'note', ...AgFns.flexWidth(100) },
      { headerName: 'System Generated', field: 'isSystemGenerated', ...AgFns.fixWidth(130) },
      {
        ...AgFns.createButtonProps('', this.onViewNote.bind(this), {
          label: 'View',
        }),
        ...AgFns.fixWidth(80),
      },
    ];
    const sortModel: ISortModel = [{ colId: 'noteTs', sort: 'desc' }];

    AgFns.initGrid(event, colDefs, sortModel);
  }

  onOk() {
    this.ref.close(null);
  }

  async onViewNote(row: ProximityUserNote) {
    const r = await ProximityUserNoteDialogComponent.openDialog(this.pngDialogService, {
      proximityUser: this.proximityUser,
      proximityUserNote: row,
      isEditable: row.isSystemGenerated ? false : this.isEditable,
    });
    if (this.isEditable) {
      AgFns.refreshGrid(this.gridOptions, this.notes);
    }
  }

  async onAddNote() {
    const r = await ProximityUserNoteDialogComponent.openDialog(this.pngDialogService, {
      proximityUser: this.proximityUser,
    });
    if (r) {
      this.notes.unshift(r);
      AgFns.refreshGrid(this.gridOptions, this.notes);
    }
  }
}
